import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { isNotifyMeEnabled } from '@/features/notify-me/lib/notify-me-eligibility';
import type { BuyInAppData, ProductButtonKind, ProductButtonReason, ProductButtonState } from './types';

type Product = ShopperProducts.schemas['Product'];
type SearchHit = ShopperSearch.schemas['ProductSearchHit'];
type Variant = ShopperProducts.schemas['Variant'];

type ProductButtonSource = Product | SearchHit;

// Products expose `inventory`/`type`/`c_*`; search hits expose `orderable`/`productType`/`c_custom`.
type SourceFields = {
    inventory?: { ats?: number; orderable?: boolean; preorderable?: boolean; backorderable?: boolean };
    orderable?: boolean;
    type?: { master?: boolean };
    productType?: { master?: boolean };
    variants?: { productId?: string }[];
    variationAttributes?: { id?: string; values?: unknown[] }[];
    id?: string;
    productId?: string;
    representedProduct?: { id?: string };
    c_isHazmatOrRestricted?: boolean;
    c_isAppExclusive?: boolean;
    c_appExclusivityUntilDate?: string;
    c_optimoveUrl?: string;
    /** Image-typed attribute: a URL string, or an object depending on how it is serialised. */
    c_optimoveQrCode?: unknown;
    c_optimove?: { url?: string; qrCodeSrc?: string };
    c_custom?: {
        isHazmatOrRestricted?: boolean;
        isRestrictedShipping?: boolean;
        isHazmat?: boolean;
        isAppExclusive?: boolean;
        appExclusivityUntilDate?: string;
        optimoveUrl?: string;
        optimoveQrCode?: string;
    };
};

export type ResolveProductButtonOptions = {
    /** Forces a kind, bypassing product data. */
    kind?: ProductButtonKind;
    /** A variant picked in the browser; its stock wins over the product's. */
    selectedVariant?: Variant | null;
    /** Quick Buy is a product-tile CTA; the PDP resolves to Add to Bag instead. */
    context?: 'pdp' | 'plp';
};

const fields = (product: ProductButtonSource): SourceFields => product as SourceFields;

const state = (kind: ProductButtonKind, reason?: ProductButtonReason): ProductButtonState => ({
    kind,
    reason,
    disabled: kind === 'out-of-stock' || reason === 'RESTRICTED',
});

/** In stock when ATS covers one unit, or the product can be pre-ordered / back-ordered. */
const isInStock = (source: SourceFields): boolean => {
    const { inventory, orderable } = source;
    if (!inventory) return orderable !== false;
    if (inventory.preorderable || inventory.backorderable) return true;
    if (inventory.orderable === false) return false;
    if (typeof inventory.ats === 'number') return inventory.ats >= 1;
    return true;
};

const isRestricted = (source: SourceFields): boolean =>
    source.c_isHazmatOrRestricted === true || source.c_custom?.isHazmatOrRestricted === true;

/**
 * App exclusivity. `appExclusivityUntilDate` is the source of truth — blank means not exclusive
 * (see the attribute's own description). Search hits instead carry `isAppExclusive`, which the
 * backend has already evaluated (date + UK locale), so the flag stands in when no date is present.
 */
const isAppExclusive = (source: SourceFields): boolean => {
    const flag = source.c_isAppExclusive === true || source.c_custom?.isAppExclusive === true;
    const until = source.c_appExclusivityUntilDate ?? source.c_custom?.appExclusivityUntilDate;
    if (!until) return flag;
    const untilTime = new Date(until).getTime();
    return Number.isNaN(untilTime) ? flag : untilTime > Date.now();
};

/** What the Quick Buy drawer can build a selector for (`use-quick-buy-attribute.ts`). */
const QUICK_BUY_ATTRIBUTE_IDS = new Set(['color', 'colour', 'shade', 'size']);

const hasMultipleVariations = (source: SourceFields): boolean => {
    const attribute = source.variationAttributes?.[0];
    if (attribute) {
        const id = attribute.id?.toLowerCase();
        return Boolean(id && QUICK_BUY_ATTRIBUTE_IDS.has(id)) && (attribute.values?.length ?? 0) > 1;
    }
    return (source.variants?.length ?? 0) > 1;
};

/**
 * Picks the product CTA from product data (SNKSN-168 acceptance table), in order:
 * explicit kind → restricted → app exclusive → Quick Buy (tiles) → Add to Bag → Notify Me → Out of Stock.
 */
export const resolveProductButton = (
    product: ProductButtonSource,
    { kind, selectedVariant, context = 'plp' }: ResolveProductButtonOptions = {}
): ProductButtonState => {
    if (kind) return state(kind);

    const source = fields(product);
    if (isRestricted(source)) return state('add-to-bag', 'RESTRICTED');
    if (isAppExclusive(source)) return state('buy-in-app', 'APP_EXCLUSIVE');

    // Variants carry runtime inventory the SCAPI schema omits.
    const inStock = isInStock(selectedVariant ? fields(selectedVariant) : source);

    if (inStock) {
        if (!selectedVariant && context !== 'pdp' && hasMultipleVariations(source)) {
            return state('quick-buy', 'VARIANT_REQUIRED');
        }
        return state('add-to-bag');
    }

    if (isNotifyMeEnabled(product)) return state('notify-me', 'OUT_OF_STOCK');
    return state('out-of-stock', 'OUT_OF_STOCK');
};

/** The product ID a CTA acts on: the selected or only variant of a master, otherwise the product itself. */
export const getActionProductId = (
    product: ProductButtonSource,
    selectedVariant?: Variant | null
): string | undefined => {
    if (selectedVariant?.productId) return selectedVariant.productId;
    const source = fields(product);
    const isMaster = Boolean(source.type?.master || source.productType?.master);
    if (isMaster && source.variants?.length === 1) return source.variants[0]?.productId;
    return source.id ?? source.representedProduct?.id ?? source.productId;
};

/** An image attribute reaches us as a URL string or as an object; accept both. */
const imageUrl = (value: unknown): string | undefined => {
    if (typeof value === 'string') return value || undefined;
    if (value && typeof value === 'object') {
        const file = value as { absUrl?: string; url?: string; link?: string; path?: string };
        return file.absUrl ?? file.url ?? file.link ?? file.path;
    }
    return undefined;
};

/** Optimove links for the Buy in App CTA, from either the product or search-hit shape. */
export const getBuyInAppData = (product: ProductButtonSource): BuyInAppData => {
    const source = fields(product);
    return {
        optimoveUrl: source.c_optimove?.url ?? source.c_optimoveUrl ?? source.c_custom?.optimoveUrl,
        qrCodeUrl:
            imageUrl(source.c_optimove?.qrCodeSrc) ??
            imageUrl(source.c_optimoveQrCode) ??
            imageUrl(source.c_custom?.optimoveQrCode),
    };
};
